import React, { useContext, useEffect, useState } from 'react';
import {
  ICventContact,
  ICventEvent,
  ICventLocation,
  IInvoice,
  useDeleteCventContactMutation,
  useGetAllCventEventsQuery,
  useToggleIsApprovedMutation,
  useGetAllCventContactsForLocationLazyQuery,
  useGetAllLocationsForEventLazyQuery,
  useUnLinkContactMutation
} from '../../generated/graphql';
import OverviewTable, { IHeadCell } from '../Common/Tables/OverviewTable';
import moment from 'moment';
import { DATE_TIME_FORMATS } from '../../enums/AppConsts';
import { AppContext } from '../../AppContext';
import { Actions } from '../../enums/ActionEnums';
import CventForm from './CventForm';
import { Box, IconButton, Switch } from '@mui/material';
import AutocompleteComponent from '../Common/Fields/AutocompleteComponent';
import { exportDataAsExcel } from '../../utils/fileUtiles';
import InvoiceStepper from './InvoiceWizard/InvoiceStepper';
import { Receipt } from '@mui/icons-material';
import CventUpload from './CventUpload';
import CventNameSelectionForm from './CventNameSelectionForm';

const CventList: React.FunctionComponent = () => {
  const { dispatch } = useContext(AppContext);
  const [filteredContacts, setFilteredContacts] = useState<ICventContact[]>([]);
  const [eventId, setEventId] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<ICventLocation | null>(null);
  const [openEventForm, setOpenEventForm] = useState(false);
  const [eventName, setEventName] = useState<ICventEvent | null>(null);
  const [deleteCventContact] = useDeleteCventContactMutation();
  const eventsData = useGetAllCventEventsQuery();
  const [allEvents, setAllEvents] = useState<ICventEvent[] | null>([]);
  const [locationsData, setLocationsData] = useState<ICventLocation[] | null>([]);

  const [toggleIsApproved] = useToggleIsApprovedMutation();
  const [getOverviewData, overviewData] = useGetAllCventContactsForLocationLazyQuery();
  const [getLocations, locationsQueryData] = useGetAllLocationsForEventLazyQuery();
  const [openCreateInvoiceForm, setOpenCreateInvoiceForm] = useState(false);
  const [openNameSelectionForm, setOpenNameSelectionForm] = useState(false);
  const [selectedCventContact, setSelectedCventContact] = useState<ICventContact | null>(null);
  const [unlinkContact] = useUnLinkContactMutation();

  const [openUpload, setOpenUpload] = useState(false);

  const handleUpdateEvents = (event: ICventEvent) => {
    if (eventName?.name !== event.name && eventName) {
      setAllEvents([event, ...(allEvents || [])]);
    }
  };

  const handleUpdateLocationContacts = (update: boolean) => {
    if (update) {
      getOverviewData({
        variables: { id: selectedLocation?.id as string },
        fetchPolicy: 'no-cache'
      });
    }
  };

  const handleUpdateLocations = (location: ICventLocation) => {
    if (location !== null) {
      setLocationsData([location, ...(locationsData || [])]);
    }
  };

  useEffect(() => {
    if (eventsData && eventsData.data?.result) {
      setAllEvents(eventsData.data.result as ICventEvent[]);
    }
  }, [eventsData]);

  useEffect(() => {
    if (locationsQueryData && locationsQueryData.data?.result) {
      setLocationsData(locationsQueryData.data.result as ICventLocation[]);
    }
  }, [locationsQueryData]);

  useEffect(() => {
    if (eventId.length > 0) {
      getLocations({ variables: { event_id: eventId } });
    } else {
      setLocationsData([]);
    }
  }, [eventId]);

  useEffect(() => {
    if (overviewData && overviewData.data?.result) {
      setFilteredContacts(overviewData.data?.result as ICventContact[]);
    }
  }, [overviewData]);

  useEffect(() => {
    if (selectedLocation) {
      getOverviewData({
        variables: { id: selectedLocation?.id as string }
      });
    }
  }, [selectedLocation]);

  const handleAddContact = (contact: ICventContact) => {
    setFilteredContacts([contact, ...(filteredContacts || [])]);
  };

  const handleEdit = (editedContact: ICventContact) => {
    setFilteredContacts(
      filteredContacts?.map((c) => (c?.id === editedContact.id ? editedContact : c))
    );
  };

  const handleEventChange = (_: unknown, newValue: React.SetStateAction<ICventEvent | null>) => {
    if (newValue) {
      setEventName(newValue);
      setEventId((newValue as ICventEvent).id);
      setSelectedLocation(null);
    } else {
      setEventName(null);
      setEventId('');
      setSelectedLocation(null);
      setFilteredContacts([]);
    }
  };

  const handleLocationChange = (
    _: unknown,
    newValue: React.SetStateAction<ICventLocation | null>
  ) => {
    if (newValue) {
      setSelectedLocation(newValue);
    } else {
      setSelectedLocation(null);
      setFilteredContacts([]);
    }
  };

  const handleExport = () => {
    const fileName = 'Cvent_Contacts';
    const csvData = filteredContacts.map((c) => ({
      Id: c.id,
      Created: c.date_created,
      Role: c.role?.name,
      Name: c.first_name,
      'Last Name': c.last_name,
      Company: c.company_name,
      Email: c.email,
      Status: c.is_approved,
      'Full Name': c.fullName,
      'Date Paid': c.date_paid,
      'Invoice Number': c.invoice
    }));
    exportDataAsExcel(csvData, fileName);
  };

  const formatDate = (rowData: ICventContact, name: string) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return rowData[name]
      ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        moment(parseInt(rowData[name] as string)).format(DATE_TIME_FORMATS.DATE_AND_TIME)
      : '';
  };

  const formatDateTwo = (rowData: IInvoice | ICventContact, name: string) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return rowData[name]
      ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        moment(parseInt(rowData[name] as string)).format(DATE_TIME_FORMATS.DATE_AND_TIME)
      : '';
  };

  const openCreateEventMethod = () => {
    setOpenEventForm(true);
  };

  const handleCloseForm = () => {
    setOpenEventForm(false);
    setSelectedCventContact(null);
  };

  const handleDetails = (rowData: ICventContact) => {
    setSelectedCventContact(rowData);
    setOpenEventForm(true);
  };

  const handleDelete = (rowData: ICventContact) => {
    if (!rowData) {
      return;
    }
    dispatch({ type: Actions.HideConfirmation });
    deleteCventContact({
      variables: {
        id: Number(rowData.id)
      },
      update(_, { data }) {
        if (data?.result) {
          dispatch({
            type: Actions.ShowMessage,
            payload: { text: `Record deleted ${rowData.fullName}`, severity: 'success' }
          });
          const updatedContacts = filteredContacts?.filter((c) => c?.id !== rowData.id);
          setFilteredContacts(updatedContacts);
        }
      }
    }).catch((e) => {
      dispatch({
        type: Actions.ShowMessage,
        payload: { text: e.message, severity: 'error' }
      });
    });
  };

  const handleRemove = (rowData: ICventContact) => {
    dispatch({
      type: Actions.ShowConfirmation,
      payload: {
        text: `Are you sure you want to delete ${rowData.fullName}?`,
        agreeAction: handleDelete,
        params: rowData
      }
    });
  };

  const handleIsApproved = (rowData: ICventContact) => {
    const is_approved = !rowData.is_approved;
    toggleIsApproved({
      variables: {
        id: rowData.id,
        input: {
          is_approved: is_approved
        }
      },
      update() {
        const message = is_approved
          ? `${rowData.fullName} is approved`
          : `${rowData.fullName} is not approved`;
        const updatedContacts = filteredContacts?.map((c) =>
          c?.id === rowData.id ? { ...c, is_approved } : c
        );
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'success',
            text: message
          }
        });
        setFilteredContacts(updatedContacts);
      }
    }).catch((e) =>
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          severity: 'error',
          text: e.message
        }
      })
    );
  };

  const isLinked = (rowData: ICventContact) => (
    <Switch
      id="is_linked_contact_switch"
      data-testid="is_linked_contact_switch"
      disableRipple
      name="checkIsLinkedContact"
      onChange={() => handleLinkContact(rowData)}
      checked={rowData.is_linked || false}
    />
  );

  const updateContact = (rowData: ICventContact, is_linked: boolean) => {
    const message = is_linked ? `${rowData.fullName} is linked` : `${rowData.fullName} is unlinked`;
    const updatedContacts = filteredContacts?.map((c) =>
      c?.id === rowData.id ? { ...c, is_linked } : c
    );

    dispatch({
      type: Actions.ShowMessage,
      payload: {
        severity: 'success',
        text: message
      }
    });
    setFilteredContacts(updatedContacts);
  };

  const handleLinkContact = (cventContact: ICventContact) => {
    const is_linked = !cventContact.is_linked;
    if (!cventContact.is_linked) {
      setSelectedCventContact(cventContact);
      setOpenNameSelectionForm(true);
    } else {
      unlinkContact({
        variables: {
          id: cventContact.id
        },
        update() {
          updateContact(cventContact, is_linked);
        }
      }).catch((e) =>
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message
          }
        })
      );
    }
  };

  const renderIsApproved = (rowData: ICventContact) => (
    <Switch
      id="is_approved_contact_switch"
      data-testid="is_approved_contact_switch"
      disableRipple
      name="checkIsApprovedContact"
      onChange={() => handleIsApproved(rowData)}
      checked={rowData.is_approved || false}
    />
  );

  const renderCreateInvoice = (rowData: ICventContact) => {
    return (
      <IconButton
        id={`${rowData.fullName}`}
        disabled={!rowData.is_linked || rowData.invoice !== null}
        onClick={() => {
          setSelectedCventContact(rowData);
          dispatch({
            type: Actions.SetInvoice,
            payload: {
              invoice: null
            }
          });
          setOpenCreateInvoiceForm(true);
        }}
      >
        <Receipt></Receipt>
      </IconButton>
    );
  };
  const columns: IHeadCell[] = [
    {
      title: 'Details',
      width: 63.5,
      sorting: false
    },
    {
      title: 'Created',
      width: 200,
      field: 'date_created',
      type: 'date',
      render: (rowData: ICventContact) => formatDate(rowData, 'date_created')
    },
    {
      title: 'Role',
      width: 160,
      field: 'role.name_short'
    },
    {
      title: 'Link',
      field: 'is_linked',
      sorting: false,
      render: isLinked,
      width: 100,
      type: 'boolean'
    },
    {
      title: 'Name',
      width: 200,
      field: 'first_name'
    },
    {
      title: 'Last Name',
      width: 200,
      field: 'last_name'
    },
    {
      title: 'Company',
      width: 200,
      field: 'company_name'
    },
    {
      title: 'Email',
      width: 195,
      field: 'email'
    },
    {
      title: 'Status',
      width: 100,
      sorting: false,
      field: 'is_approved',
      render: renderIsApproved,
      type: 'boolean'
    },
    { title: 'Full Name', width: 185, field: 'fullName' },
    {
      title: 'Date Paid',
      width: 200,
      field: 'date_paid',
      type: 'date',
      render: (rowData: ICventContact) =>
        formatDateTwo(rowData.invoice ? rowData.invoice : rowData, 'date_paid')
    },
    { title: 'Invoice Number', width: 70, field: 'invoice.number' },
    {
      title: 'Create Invoice',
      width: 30,
      sorting: false,
      render: renderCreateInvoice
    },
    {
      title: 'Remove',
      width: 71.5,
      sorting: false
    }
  ];
  return (
    <Box className="overviewPage">
      <Box>
        <OverviewTable
          columns={columns}
          data={filteredContacts}
          loading={overviewData.loading}
          pageSize={10}
          handleRemove={handleRemove}
          handleDetails={handleDetails}
          createButtonProps={{
            id: 'create-registrant',
            onClick: openCreateEventMethod,
            text: 'Create Registrant',
            disabled: selectedLocation === null || eventName === null
          }}
          importButtonProps={{
            id: 'import-ftse-button',
            onClick: () => setOpenUpload(true)
          }}
          exportButtonProps={{ id: 'export-contacts--button', onClick: () => handleExport() }}
          additionalFilters={
            <Box id="fitlers" sx={{ display: 'flex', gap: '40px', marginLeft: '20px' }}>
              <AutocompleteComponent
                id="cvent-event-autocomplete-filter"
                label="Event Name"
                options={(allEvents as ICventEvent[]) || []}
                values={eventName}
                onChange={handleEventChange}
                getOptionLabel={(option: ICventEvent) => option?.name as string}
                additionalStyle={{ width: '280px' }}
              />
              <AutocompleteComponent
                id="cvent-locations-autocomplete-filter"
                label="Event Location"
                options={(locationsData as never[]) || []}
                values={selectedLocation}
                onChange={handleLocationChange}
                getOptionLabel={(option: ICventLocation) => option?.name as string}
                additionalStyle={{ width: '280px' }}
                disabled={locationsData?.length === 0}
              />
            </Box>
          }
        />
        {openEventForm && (
          <CventForm
            handleClose={handleCloseForm}
            cventContact={selectedCventContact}
            locationId={Number(selectedLocation?.id)}
            updateList={handleAddContact}
            editList={handleEdit}
          />
        )}
        {openCreateInvoiceForm && (
          <InvoiceStepper
            cventContact={selectedCventContact as ICventContact}
            onClose={() => setOpenCreateInvoiceForm(false)}
            handleUpdateLocationContacts={handleUpdateLocationContacts}
          />
        )}
        {openUpload && (
          <CventUpload
            handleClose={() => setOpenUpload(false)}
            updateEvents={handleUpdateEvents}
            updateLocations={handleUpdateLocations}
          />
        )}
        {openNameSelectionForm && (
          <CventNameSelectionForm
            handleClose={() => setOpenNameSelectionForm(false)}
            cventContact={selectedCventContact as ICventContact}
          />
        )}
      </Box>
    </Box>
  );
};

export default CventList;
